import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Camera,
  Check,
  Grid3x3,
  ImagePlus,
  MessageSquareText,
  Rows3,
  Settings2,
  Tag,
  Users,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState, type DragEvent, type FormEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate, useParams } from 'react-router'
import { AlbumViews } from '@/components/AlbumViews'
import { UploadTray } from '@/components/AddPhotos'
import { Choice } from '@/components/Choice'
import { ConfirmButton } from '@/components/ConfirmButton'
import { DatePicker } from '@/components/DatePicker'
import { ModernAlbumPhotos, ModernAlbumTop } from '@/components/modern/ModernAlbum'
import { Wall } from '@/components/modern/Wall'
import { BarRow } from '@/components/nav/BarRow'
import { FormError } from '@/components/FormError'
import { page } from '@/components/layout'
import { PhotoGrid, type GridLayout } from '@/components/PhotoGrid'
import { CreateAction } from '@/components/CreateAction'
import { NewAlbum } from '@/components/NewAlbum'
import { PickedBar, SelectToggle } from '@/components/Selection'
import { SendPhotos } from '@/components/SendTo'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useAddPhotos } from '@/lib/addPhotos'
import { api } from '@/lib/api'
import {
  albumPhotoUrl,
  dates,
  organize,
  peopleIn,
  splitTags,
  useAlbum,
  useAlbums,
  type Album,
  type Organize,
} from '@/lib/albums'
import { useFamily } from '@/lib/families'
import { useFrame, useItemHome, usePageFrame } from '@/lib/frame'
import { saveAlbumView, savedAlbumView, type AlbumView } from '@/lib/albumView'
import { useIsPhone } from '@/lib/platform'
import { usePhotoLink } from '@/lib/photoMotion'
import { forgetVisit, rememberVisit } from '@/lib/recent'
import { takeHandedOffPhotos } from '@/lib/handoff'
import { tint } from '@/lib/paper'
import { useSelection } from '@/lib/selection'
import { PHOTO_TYPES } from '@/lib/photos'
import { useTree } from '@/lib/tree'
import { useMe } from '@/lib/useMe'
import { cn } from '@/lib/utils'
import { ShareWith } from '@/components/ShareWith'
import { audienceBody, audienceFrom, audienceSaid, type Audience } from '@/lib/spaces'
import { PageLoader } from '@/components/Loader'

export function AlbumPage() {
  const { id = '' } = useParams()
  const album = useAlbum(id)
  if (album.isPending) return <PageLoader />
  if (album.isError) return <FormError error={album.error} />
  return <AlbumView album={album.data} />
}

type Mode = 'people' | 'tags' | 'date'
// After photos go in, the album walks you through making them easy to find. Every step can be skipped.
type Coach = { ids: string[]; done: Set<Mode | 'captions'> }

const LAYOUT_KEY = 'gatheredfrom:album-layout'
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

function AlbumView({ album }: { album: Album }) {
  const navigate = useNavigate()
  const me = useMe().data
  // In the Modern style of Photos the owner can pick, the album reads like a profile over a wall of whole photos.
  const modern = me?.photosStyle === 'modern'
  const family = useFamily(album.familyId)
  const collections = useAlbums(album.familyId).data?.collections ?? []
  const count = album.photos.length
  const selection = useSelection()
  const { selecting, selected } = selection
  const [sending, setSending] = useState(false)
  const [starting, setStarting] = useState(false)
  const [organizing, setOrganizing] = useState<{ mode: Mode; photos: string[]; coached: boolean } | null>(null)
  const [coach, setCoach] = useState<Coach | null>(null)
  const [settings, setSettings] = useState(false)
  const [dragging, setDragging] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const adding = useAddPhotos({ albumId: album.id }, album.familyId, (ids) => setCoach({ ids, done: new Set() }))
  const camera = useRef<HTMLInputElement>(null)
  const phone = useIsPhone()
  const { actionSlot } = useFrame()
  const photoLink = usePhotoLink()
  const [view, setView] = useState<AlbumView>(savedAlbumView)
  function changeView(next: AlbumView) {
    setView(next)
    saveAlbumView(next)
  }
  const [layout, setLayout] = useState<GridLayout>(() => {
    try {
      return localStorage.getItem(LAYOUT_KEY) === 'squares' ? 'squares' : 'rows'
    } catch {
      return 'rows'
    }
  })
  function changeLayout(next: GridLayout) {
    setLayout(next)
    try {
      localStorage.setItem(LAYOUT_KEY, next)
    } catch {
      // Only a preference; without storage the album opens in rows next time.
    }
  }
  // Only people in its own circle add to it or organize it; someone it's shared with from another circle looks.
  const canAdd = album.canAdd !== false
  // Back to the photos of the place it was opened from, which may be another circle it's shared into, or Everyone.
  const home = useItemHome(album)
  const photosPage = canAdd || home !== album.familyId ? `/circle/${home}/photos` : '/'
  const forWhom = album.sharedWith ? audienceSaid(album, album.mine ? 'Only you' : '') : (album.space?.name ?? '')
  const taken = album.photos.flatMap((x) => (x.takenOn ? [x.takenOn] : [])).sort()
  const when = dates(taken[0] ?? null, taken.at(-1) ?? null)
  usePageFrame(home, {
    title: album.title,
    back: { to: photosPage, label: photosPage !== '/' ? 'Back to photos' : 'Back' },
  })
  useEffect(
    () => rememberVisit(album.familyId, { to: `/album/${album.id}`, title: album.title, kind: 'album' }),
    [album.familyId, album.id, album.title],
  )

  const stopSelecting = selection.stop
  function choose(files: File[]) {
    if (files.length === 0) return
    setCoach(null)
    adding.add(files)
  }
  const carriesFiles = (e: DragEvent) => e.dataTransfer.types.includes('Files')

  const pick = () => input.current?.click()
  // Photos picked on the Photos page before this album was chosen go in as soon as it opens.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- it starts their uploads, as picking them here would
    choose(takeHandedOffPhotos(album.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, for this album
  }, [album.id])
  return (
    <div
      className="relative flex flex-col gap-5 pt-2 pb-10 nav:gap-10 nav:pt-12"
      onDragOver={(e) => {
        if (!canAdd || !carriesFiles(e)) return
        e.preventDefault()
        setDragging(true)
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false)
      }}
      onDrop={(e) => {
        if (!canAdd || !carriesFiles(e)) return
        e.preventDefault()
        setDragging(false)
        choose([...e.dataTransfer.files])
      }}
    >
      <input
        ref={input}
        type="file"
        accept={PHOTO_TYPES}
        multiple
        className="sr-only"
        data-testid="choose-album-photos"
        aria-label="Choose photos to add"
        onChange={(e) => {
          choose([...(e.target.files ?? [])])
          e.target.value = ''
        }}
      />

      <input
        ref={camera}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        aria-label="Take a photo to add"
        onChange={(e) => {
          choose([...(e.target.files ?? [])])
          e.target.value = ''
        }}
      />

      {/* On a phone the album's views go up into the top bar, and its settings into the bar's actions beside the "+",
          so the album starts right under them (Dallas, 2026-10-18). */}
      {phone && count > 0 && (
        <BarRow barClassName="flex min-w-0 flex-1 justify-end">
          <AlbumViews albumId={album.id} view={view} onView={changeView} story glass />
        </BarRow>
      )}
      {phone &&
        album.mine &&
        actionSlot &&
        createPortal(
          <button
            type="button"
            aria-label="Album settings"
            title="Album settings"
            onClick={() => setSettings(true)}
            className="order-first grid size-11 shrink-0 place-items-center rounded-full text-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:opacity-60 android:size-12"
          >
            <Settings2 className="size-6 stroke-[1.75]" aria-hidden />
          </button>,
          actionSlot,
        )}
      {count > 0 && canAdd && phone && <SelectToggle selection={selection} />}
      {!selecting && canAdd && (
        // The top bar's "+" asks camera or library.
        <CreateAction
          section="photos"
          title="Add photos"
          actions={[
            { label: 'Take a photo', icon: Camera, onSelect: () => camera.current?.click() },
            { label: 'Choose photos', icon: ImagePlus, onSelect: pick },
          ]}
        />
      )}

      <header className={cn(page, 'flex flex-col gap-4 nav:gap-6')}>
        {!phone && (
          <div className="flex items-center justify-end gap-2">
            <Link
              to={photosPage}
              className="mr-auto inline-flex h-10 items-center gap-1.5 rounded-full bg-secondary px-4 text-sm font-medium transition-colors outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <ArrowLeft className="size-4" aria-hidden /> Photos
            </Link>
            {count > 0 && <AlbumViews albumId={album.id} view={view} onView={changeView} story />}
            {count > 0 && canAdd && <SelectToggle selection={selection} />}
            {album.mine && (
              <Button
                variant="outline"
                size="icon"
                className="size-12"
                aria-label="Album settings"
                onClick={() => setSettings(true)}
              >
                <Settings2 aria-hidden />
              </Button>
            )}
          </div>
        )}
        {modern ? (
          <ModernAlbumTop album={album} when={when} forWhom={forWhom} about={<About album={album} start />} />
        ) : (
          <>
            <div className="flex flex-col items-center gap-3 text-center nav:items-start nav:text-left">
              {album.collection && <p className="eyebrow">{album.collection.title}</p>}
              <h1 className="text-display text-balance">{album.title}</h1>
              <About album={album} />
              {(when || forWhom) && (
                <p className="flex flex-wrap justify-center gap-x-2 text-sub text-muted-foreground">
                  {when && <span>{when}</span>}
                  {when && forWhom && <span aria-hidden="true">·</span>}
                  {forWhom && <span>For {forWhom}</span>}
                </p>
              )}
            </div>
            {count > 0 && <AlbumBar album={album} layout={view === 'tiles' ? layout : null} onLayout={changeLayout} />}
          </>
        )}
      </header>

      <div className={cn(page, 'flex flex-col gap-8')}>
        <UploadTray adding={adding} />
        {coach && (
          <CoachCard
            coach={coach}
            onStep={(step) =>
              step === 'captions'
                ? navigate(`/photo/${album.photos.find((x) => coach.ids.includes(x.id))?.id ?? coach.ids[0]}?caption=1`)
                : setOrganizing({ mode: step, photos: coach.ids, coached: true })
            }
            onClose={() => setCoach(null)}
          />
        )}
        {count === 0 ? (
          adding.pending === 0 &&
          (canAdd ? (
            <DropZone title={album.title} onPick={pick}>
              {album.mine && (
                <Button variant="ghost" onClick={() => setSettings(true)} className="self-center">
                  <Settings2 aria-hidden /> Album settings
                </Button>
              )}
            </DropZone>
          ) : (
            <p className="text-center text-muted-foreground">No photos in this album yet.</p>
          ))
        ) : modern ? (
          <div className={cn('max-sm:-mx-4', view === 'feed' && 'sm:mx-auto sm:w-full sm:max-w-2xl')}>
            <ModernAlbumPhotos
              album={album}
              selection={selection}
              moments={me?.sections.moments ?? false}
              feed={view === 'feed'}
            />
          </div>
        ) : view === 'feed' ? (
          <div className="max-sm:-mx-4 sm:mx-auto sm:w-full sm:max-w-2xl">
            <Wall
              photos={album.photos}
              label="Photos in this album"
              selected={selected}
              onToggle={selecting ? selection.toggle : undefined}
              onOpen={(id) => {
                const { to, state } = photoLink(id)
                navigate(to, { state })
              }}
              feed
            />
          </div>
        ) : (
          <section aria-label="Photos in this album">
            <PhotoGrid
              photos={album.photos}
              layout={layout}
              selected={selected}
              onToggle={selecting ? selection.toggle : undefined}
            />
          </section>
        )}
      </div>

      {dragging && (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-3 z-40 grid place-items-center rounded-[2.5rem] border-2 border-dashed border-primary bg-background/85"
        >
          <p className="flex items-center gap-3 text-title font-semibold tracking-tight">
            <ImagePlus className="size-7" /> Drop to add to {album.title}
          </p>
        </div>
      )}

      {selecting && (
        <OrganizeBar
          album={album}
          selected={[...selected]}
          onOrganize={(mode) => setOrganizing({ mode, photos: [...selected], coached: false })}
          onSend={() => setSending(true)}
          onSelectAll={() => selection.pickAll(album.photos.map((x) => x.id))}
          onDone={stopSelecting}
        />
      )}

      <SendPhotos
        open={sending && !starting}
        onOpenChange={setSending}
        familyId={album.familyId}
        photos={[...selected]}
        from={album.id}
        inAlbum
        onSent={() => {
          setSending(false)
          stopSelecting()
        }}
        onNewAlbum={() => setStarting(true)}
      />
      {starting && family.data && (
        <NewAlbum
          open
          onOpenChange={(open: boolean) => !open && setStarting(false)}
          family={family.data}
          space={album.space}
          collections={collections}
          first={false}
          send={[...selected]}
        />
      )}

      <Dialog open={!!organizing} onOpenChange={(open) => !open && setOrganizing(null)}>
        <DialogContent className="max-h-[85svh] overflow-y-auto">
          {organizing && (
            <OrganizeForm
              album={album}
              mode={organizing.mode}
              photos={organizing.photos}
              onDone={() => {
                if (organizing.coached) {
                  setCoach((c) => c && { ...c, done: new Set([...c.done, organizing.mode]) })
                } else {
                  stopSelecting()
                }
                setOrganizing(null)
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {album.mine && (
        <Dialog open={settings} onOpenChange={setSettings}>
          <DialogContent className="max-h-[90svh] overflow-y-auto">
            <AlbumSettings album={album} onSaved={() => setSettings(false)} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

// The few words the album's keeper wrote about it, folded after three lines. Written in the album's settings.
function About({ album, start }: { album: Album; start?: boolean }) {
  const [open, setOpen] = useState(false)
  if (!album.description) return null
  const long = album.description.length > 180 || album.description.split('\n').length > 3
  return (
    <div className={cn('flex max-w-2xl flex-col items-center gap-1 nav:items-start', start && 'items-start')}>
      <p className={cn('text-pretty whitespace-pre-line', long && !open && 'line-clamp-3')}>{album.description}</p>
      {long && (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className="text-sm font-medium text-brand underline underline-offset-4 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}

// Who's in the album, how many photos it holds, and how they're laid out.
function AlbumBar({
  album,
  layout,
  onLayout,
}: {
  album: Album
  // Missing while the photos show as a feed, which has no layouts of its own.
  layout: GridLayout | null
  onLayout: (layout: GridLayout) => void
}) {
  const people = peopleIn(album.photos)
  const shown = people.slice(0, 6)
  const toggle = (value: GridLayout, label: string, icon: ReactNode) => (
    <button
      type="button"
      aria-pressed={layout === value}
      aria-label={label}
      onClick={() => onLayout(value)}
      className="grid size-9 place-items-center rounded-full text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-pressed:bg-brand aria-pressed:text-brand-foreground pointer-coarse:size-11"
    >
      {icon}
    </button>
  )
  return (
    <div className="flex items-center gap-3 rounded-[1.75rem] border px-4 py-3 sm:gap-5 sm:px-5">
      {people.length > 0 && (
        <div className="flex min-w-0 items-center gap-3">
          <p className="hidden shrink-0 text-sm leading-tight font-medium sm:block">
            People
            <span className="block font-normal text-muted-foreground">in this album</span>
          </p>
          <ul className="flex min-w-0 items-center gap-1.5" aria-label="People in this album">
            {shown.map((p, i) => (
              <li key={p.id} className={cn(i >= 4 && 'max-sm:hidden')}>
                <Link
                  to={`/circle/${album.familyId}/photos?${new URLSearchParams({ person: p.id })}`}
                  aria-label={`Photos of ${p.name}`}
                  title={p.name}
                  className="block rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  <Avatar person={p} className="size-9 text-base shadow-none ring-2 ring-background sm:size-10" />
                </Link>
              </li>
            ))}
            {people.length > shown.length && (
              <li className="text-xs text-muted-foreground">+{people.length - shown.length}</li>
            )}
          </ul>
        </div>
      )}
      <span className="flex-1" />
      <p className="shrink-0 text-sm text-muted-foreground">{plural(album.photos.length, 'photo')}</p>
      {layout && (
        <div role="group" aria-label="Layout" className="flex shrink-0 gap-0.5 rounded-full bg-secondary p-0.5">
          {toggle('rows', 'Show in rows', <Rows3 className="size-4" aria-hidden />)}
          {toggle('squares', 'Show as squares', <Grid3x3 className="size-4" aria-hidden />)}
        </div>
      )}
    </div>
  )
}

function DropZone({ title, onPick, children }: { title: string; onPick: () => void; children?: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={onPick}
        className="group flex flex-col items-center gap-5 rounded-[2.5rem] border-2 border-dashed border-input px-6 py-14 text-center transition-colors outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:py-20"
      >
        <span className="grid size-16 place-items-center rounded-full bg-secondary transition-colors group-hover:bg-background">
          <ImagePlus className="size-7" strokeWidth={1.6} aria-hidden />
        </span>
        <span className="flex max-w-md flex-col gap-2">
          <span className="text-title font-semibold tracking-tight text-balance">Add the photos from {title}.</span>
          <span className="text-muted-foreground">
            Choose them all at once, or drop them here. Who’s in them, the dates and the captions can come after.
          </span>
        </span>
        <span className="inline-flex h-11 items-center gap-2 rounded-full bg-primary px-6 font-medium text-primary-foreground">
          Choose photos
        </span>
      </button>
      {children}
    </div>
  )
}

const COACH_STEPS = [
  { step: 'people', icon: Users, title: 'Tag who’s in them', hint: 'Living or in memory' },
  { step: 'tags', icon: Tag, title: 'Add tags', hint: 'Words to find them by' },
  { step: 'captions', icon: MessageSquareText, title: 'Write captions', hint: 'One photo at a time' },
] as const

function CoachCard({
  coach,
  onStep,
  onClose,
}: {
  coach: Coach
  onStep: (step: 'people' | 'tags' | 'captions') => void
  onClose: () => void
}) {
  const n = coach.ids.length
  return (
    <section aria-labelledby="coach-title" className="flex flex-col gap-5 rounded-[2rem] table-surface p-5 sm:p-7">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1" role="status">
          <h2 id="coach-title" className="text-title font-semibold tracking-tight">
            {plural(n, 'photo')} added.
          </h2>
          <p className="text-table-muted">
            Now help everyone find {n === 1 ? 'it' : 'them'}. Each step is optional, and you can come back to it later.
          </p>
        </div>
        <Button variant="ghost" size="icon" aria-label="Done for now" onClick={onClose}>
          <X aria-hidden />
        </Button>
      </div>
      <ol className="grid gap-2 sm:grid-cols-3">
        {COACH_STEPS.map(({ step, icon: Icon, title, hint }, i) => {
          const done = coach.done.has(step)
          return (
            <li key={step}>
              <button
                type="button"
                onClick={() => onStep(step)}
                className="flex w-full items-center gap-3 rounded-2xl bg-paper p-3 text-left shadow-paper transition-[translate,box-shadow] outline-none hover:-translate-y-0.5 hover:shadow-lift focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'grid size-10 shrink-0 place-items-center rounded-full',
                    done ? 'bg-primary text-primary-foreground' : 'bg-secondary',
                  )}
                >
                  {done ? <Check className="size-5" /> : <Icon className="size-5" />}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="font-medium">
                    <span className="sr-only">Step {i + 1}: </span>
                    {title}
                    {done && <span className="sr-only"> (done)</span>}
                  </span>
                  <span className="text-sub text-muted-foreground">{hint}</span>
                </span>
              </button>
            </li>
          )
        })}
      </ol>
    </section>
  )
}

// Upload first, organize after: what's picked can be tagged, dated or sent to another album together.
function OrganizeBar({
  album,
  selected,
  onOrganize,
  onSend,
  onSelectAll,
  onDone,
}: {
  album: Album
  selected: string[]
  onOrganize: (mode: Mode) => void
  onSend: () => void
  onSelectAll: () => void
  onDone: () => void
}) {
  const picked = album.photos.filter((x) => selected.includes(x.id))
  const canChange = picked.length > 0 && picked.every((x) => x.canEdit)
  return (
    <PickedBar
      label="Organize the photos you picked"
      count={picked.length}
      noun="photo"
      actions={[
        { label: 'Who’s in them', onSelect: () => onOrganize('people') },
        { label: 'Tags', onSelect: () => onOrganize('tags') },
        ...(canChange
          ? [
              { label: 'Date', onSelect: () => onOrganize('date') },
              { label: 'Send to album', onSelect: onSend },
            ]
          : []),
      ]}
      onPickAll={onSelectAll}
      onDone={onDone}
    />
  )
}

const TITLES: Record<Mode, string> = {
  people: 'Who’s in these photos?',
  tags: 'Tag these photos',
  date: 'When were these taken?',
}

function OrganizeForm({
  album,
  mode,
  photos,
  onDone,
}: {
  album: Album
  mode: Mode
  photos: string[]
  onDone: () => void
}) {
  const queryClient = useQueryClient()
  const save = useMutation({
    mutationFn: (change: Organize) => organize(album.familyId, photos, change),
    onSuccess: async () => {
      for (const key of ['album', 'albums', 'photo', 'found-photos', 'person']) {
        await queryClient.invalidateQueries({ queryKey: [key] })
      }
      onDone()
    },
  })
  const n = photos.length
  const refOf = new Map(album.photos.map((x) => [x.id, x.ref]))
  return (
    <>
      <DialogHeader>
        <DialogTitle>{TITLES[mode]}</DialogTitle>
        <DialogDescription>For the {n === 1 ? 'photo' : `${n} photos`} you picked.</DialogDescription>
      </DialogHeader>
      <ul aria-hidden="true" className="-mt-1 scroll-row gap-1">
        {photos.slice(0, 12).map((id, i) => (
          <li key={id} className="shrink-0">
            <img
              src={albumPhotoUrl(refOf.get(id) ?? id, 'thumb')}
              alt=""
              className={cn('size-12 rounded-md object-cover', tint(i))}
            />
          </li>
        ))}
      </ul>
      {mode === 'people' && (
        <PickPeople album={album} pending={save.isPending} onSave={(ids) => save.mutate({ addPeople: ids })} />
      )}
      {mode === 'tags' && (
        <PickTags album={album} pending={save.isPending} onSave={(tags) => save.mutate({ addTags: tags })} />
      )}
      {mode === 'date' && <PickDate pending={save.isPending} onSave={(takenOn) => save.mutate({ takenOn })} />}
      <FormError error={save.error} />
    </>
  )
}

// People from the tree, living or not: great-grandma belongs in the old photos. Tagging is by hand.
function PickPeople({ album, pending, onSave }: { album: Album; pending: boolean; onSave: (ids: string[]) => void }) {
  const tree = useTree(album.familyId)
  const [find, setFind] = useState('')
  const [chosen, setChosen] = useState<Set<string>>(new Set())
  const people = (tree.data?.people ?? []).filter((p) => p.name.toLowerCase().includes(find.trim().toLowerCase()))
  if (tree.data && tree.data.people.length === 0) {
    return (
      <p className="text-muted-foreground">
        No one is on the tree yet. Add people from{' '}
        <Link to={`/circle/${album.familyId}/people`} className="text-brand underline underline-offset-4">
          People
        </Link>
        , then tag them here.
      </p>
    )
  }
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        onSave([...chosen])
      }}
    >
      {(tree.data?.people.length ?? 0) > 8 && (
        <Input
          aria-label="Find someone"
          placeholder="Find someone"
          value={find}
          onChange={(e) => setFind(e.target.value)}
        />
      )}
      <fieldset className="flex flex-col gap-1">
        <legend className="sr-only">People on the tree</legend>
        {people.map((p) => (
          <label
            key={p.id}
            className="flex min-h-14 cursor-pointer items-center gap-3 rounded-2xl px-2 transition-colors hover:bg-secondary has-checked:bg-secondary has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50"
          >
            <Avatar person={p} className="size-10 text-lg shadow-none" />
            <span className="flex-1">
              {p.name}
              {!p.living && <span className="text-muted-foreground"> (in memory)</span>}
            </span>
            <span className="relative mr-1 grid size-6 place-items-center">
              <input
                type="checkbox"
                className="peer size-6 cursor-pointer appearance-none rounded-full border-2 border-input outline-none checked:border-brand checked:bg-brand"
                checked={chosen.has(p.id)}
                onChange={() => {
                  const next = new Set(chosen)
                  if (next.has(p.id)) next.delete(p.id)
                  else next.add(p.id)
                  setChosen(next)
                }}
              />
              <Check
                aria-hidden="true"
                strokeWidth={3}
                className="pointer-events-none absolute size-3.5 text-primary-foreground opacity-0 peer-checked:opacity-100"
              />
            </span>
          </label>
        ))}
      </fieldset>
      <Button type="submit" size="lg" disabled={pending || chosen.size === 0} className="self-start">
        Tag {chosen.size > 0 ? chosen.size : ''} {chosen.size === 1 ? 'person' : 'people'}
      </Button>
    </form>
  )
}

function PickTags({ album, pending, onSave }: { album: Album; pending: boolean; onSave: (tags: string[]) => void }) {
  const albums = useAlbums(album.familyId)
  const [text, setText] = useState('')
  const tags = splitTags(text)
  const known = (albums.data?.tags ?? []).map((x) => x.tag).filter((x) => !tags.includes(x))
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        onSave(tags)
      }}
    >
      <div className="grid gap-2">
        <Label htmlFor="add-tags">Tags</Label>
        <Input id="add-tags" value={text} onChange={(e) => setText(e.target.value)} />
        <p className="text-sub text-muted-foreground">Separate tags with commas.</p>
      </div>
      {known.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="eyebrow">Used before</p>
          <div className="flex flex-wrap gap-2">
            {known.slice(0, 20).map((tag) => (
              <Button
                key={tag}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setText([...tags, tag].join(', '))}
              >
                {tag}
              </Button>
            ))}
          </div>
        </div>
      )}
      <Button type="submit" size="lg" disabled={pending || tags.length === 0} className="self-start">
        Add tags
      </Button>
    </form>
  )
}

// A date as rough as the family knows it. Setting one replaces what the camera said, for every photo picked.
export function PickDate({
  initial = '',
  pending,
  onSave,
}: {
  initial?: string
  pending: boolean
  onSave: (takenOn: string | null) => void
}) {
  const [date, setDate] = useState<string | null>(initial || null)
  function submit(e: FormEvent) {
    e.preventDefault()
    if (date) onSave(date)
  }
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <div className="grid gap-2">
        <Label htmlFor="taken-on">Taken on</Label>
        <DatePicker id="taken-on" value={date} onChange={setDate} aria-describedby="taken-on-hint" />
        <p id="taken-on-hint" className="text-sub text-muted-foreground">
          A year is enough if that’s all anyone knows.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="lg" disabled={pending || !date}>
          Save date
        </Button>
        <Button type="button" variant="ghost" size="lg" disabled={pending} onClick={() => onSave(null)}>
          Clear the date
        </Button>
      </div>
    </form>
  )
}

// Looking after the album: its name, who it's for, its collection, and taking it down.
function AlbumSettings({ album, onSaved }: { album: Album; onSaved: () => void }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const albums = useAlbums(album.familyId)
  const [title, setTitle] = useState(album.title)
  const [about, setAbout] = useState(album.description)
  const [who, setWho] = useState<Audience>(() =>
    album.sharedWith ? audienceFrom(album) : { everyone: false, spaceIds: album.space ? [album.space.id] : [] },
  )
  const [collectionId, setCollectionId] = useState(album.collection?.id ?? '')
  const save = useMutation({
    mutationFn: () =>
      api(`/albums/${album.id}`, {
        method: 'PATCH',
        body: {
          title: title.trim(),
          description: about.trim(),
          ...audienceBody(who),
          collectionId: collectionId || null,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['album', album.id] })
      await queryClient.invalidateQueries({ queryKey: ['albums', album.familyId] })
      onSaved()
    },
  })
  const remove = useMutation({
    mutationFn: () => api(`/albums/${album.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      forgetVisit(album.familyId, `/album/${album.id}`)
      await queryClient.invalidateQueries({ queryKey: ['albums', album.familyId] })
      navigate(`/circle/${album.familyId}/photos`, { replace: true })
    },
  })
  const collections = albums.data?.collections ?? []
  return (
    <div className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>Album settings</DialogTitle>
        <DialogDescription>Only you can change these, as the person who made this album.</DialogDescription>
      </DialogHeader>
      <form
        className="grid gap-5"
        onSubmit={(e) => {
          e.preventDefault()
          save.mutate()
        }}
      >
        <Field label="Album name" htmlFor="album-rename">
          <Input id="album-rename" required maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="About this album" htmlFor="album-about">
          <Textarea id="album-about" maxLength={1000} value={about} onChange={(e) => setAbout(e.target.value)} />
        </Field>
        <div className="grid gap-2">
          <p className="text-sm font-medium">Who’s this for?</p>
          <ShareWith here={album.familyId} value={who} onChange={setWho} onlyMeHint="Just you, until you share it" />
          <p className="text-sub text-muted-foreground">Everyone who sees the album sees every photo in it.</p>
        </div>
        {collections.length > 0 && (
          <Choice
            legend="Collection"
            value={collectionId}
            options={[['', 'None'], ...collections.map((x): [string, string] => [x.id, x.title])]}
            onChange={setCollectionId}
          />
        )}
        <FormError error={save.error} />
        <Button type="submit" size="lg" disabled={save.isPending} className="self-start">
          Save changes
        </Button>
      </form>
      <div className="flex flex-col gap-2 border-t pt-5">
        <FormError error={remove.error} />
        <ConfirmButton
          label="Delete this album"
          title={`Delete ${album.title}?`}
          description="The album and its photos will be removed for everyone it’s shared with."
          confirm="Delete album"
          disabled={remove.isPending}
          onConfirm={() => remove.mutate()}
        />
      </div>
    </div>
  )
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  )
}
